/**
 * Amended returns (owner decision 2026-10-16): a customer whose return for a
 * past tax year was already filed, often by another provider, with an error
 * buys a corrected filing from us at the late price (`pricing.ts`).
 *
 * What the IRS says, read 2026-10-16:
 * - i5472 (https://www.irs.gov/instructions/i5472), "When and Where To File",
 *   "Foreign-owned U.S. DEs": the DE files "a pro forma Form 1120 ... with
 *   Form 5472 attached", "'Foreign-owned U.S. DE' should be written across the
 *   top of the Form 1120", by fax to 855-887-7737 or mail to the Ogden PIN
 *   Unit. Neither i5472 nor Form 5472 (Rev. 12-2023) nor Form 1120 (2025, item
 *   E has no amended box) says how a DE corrects a filed 5472. UNCONFIRMED: an
 *   IRS procedure for it. Our practice is a complete replacement pro forma 1120
 *   + 5472 marked "AMENDED" beside the IRS legend, with an explanation of the
 *   changes (the statement i1065 "Amended Return" asks a partnership for), sent
 *   the way an original is (an inference, not stated by the IRS).
 * - i1120 (https://www.irs.gov/instructions/i1120), "Amended return": "Use
 *   Form 1120-X ... to correct a previously filed Form 1120." The engine does
 *   not fill Form 1120-X, so a C-corp amendment is prepared by hand.
 * - i1065 (https://www.irs.gov/instructions/i1065), "Amended Return": a paper
 *   filer completes "Form 1065-X"; we mail paper, so a partnership amendment is
 *   prepared by hand too.
 *
 * A multi-member LLC's amendment is refused before payment (PG-22, read
 * 2026-10-17; research/mmllc-k2k3.md "PG-22: amended returns"). Most of our
 * partnerships are under the BBA centralized audit regime: a late return cannot
 * elect out (i1065 2025 p. 30, "can only be made on a timely filed return"), and
 * electing out needs every member's correct U.S. TIN on Schedule B-2; an on-time
 * return whose members all have one elects out unless the customer declines
 * (PG-19, `mmllc-bba.ts` in the API). A partnership under the regime cannot
 * amend: "A partnership that is subject to the BBA centralized
 * partnership audit regime must file an AAR ... A BBA partnership filing an AAR
 * shouldn't file an amended tax return or amended Schedules K-1 and/or K-3"
 * (i1065 2025, "Administrative Adjustment Request (AAR)"; 2023 and 2024 the
 * same). On paper the AAR is Form 1065-X: "If you're a BBA partnership filing
 * an AAR, Form 1065-X should only be used to make a paper filing. For
 * electronic filing, use Form 8082 in conjunction with Form 1065" (i1065x Rev.
 * 10-2025 p. 1). It "must be signed under penalties of perjury by the
 * partnership representative" (Reg. 301.6227-1(c)(1)), not a member; it "must
 * always include a computation of the IU" (imputed underpayment, i1065x p. 8),
 * which the partnership pays when it files unless it pushes the adjustments out
 * to the members; and Forms 8985 and 8986 "are required to be filed with the
 * AAR" whenever the adjustments are pushed out or produce no imputed
 * underpayment (i1065x p. 7), each Form 8986 naming the member's TIN (Reg.
 * 301.6227-1(e)). The deadline is 3 years after the later of the filing date or
 * the original due date, and never after a notice of administrative proceeding
 * (IRC 6227(c)). A partnership that did elect out files Form 1065-X as an
 * amended return with amended Schedules K-1 (i1065x p. 1 and pp. 6-7). The
 * engine fills none of these, the representative who must sign is a third
 * party, and the push-out statements need TINs most members lack, so the
 * customer is told what is needed instead of paying for a hand build.
 */
import { irsCalendarYear } from "./deadlines";
import type { FederalTaxFilingMode } from "./pricing";
import {
  entityKindOfScenario,
  isEntityKind,
  type EntityKind,
  type Scenario,
} from "./scenario";

/** `documents.type` of the customer's copy of the return as it was filed. */
export const AMENDMENT_ORIGINAL_RETURN_CATEGORY = "federal_tax_original_return";

export const AMENDMENT_WHAT_WAS_WRONG_MIN = 10;
export const AMENDMENT_WHAT_WAS_WRONG_MAX = 2000;

export const FILING_MODE_LABEL: Readonly<Record<FederalTaxFilingMode, string>> =
  {
    standard: "On-time filing",
    late: "Late filing",
    amended: "Amended return",
  };

/**
 * The past tax years a late or amended filing may be bought for: 2020 or five
 * years back, whichever is later, through last year (the IRS calendar's).
 * Which of them we build is `isTaxYearOffered` (`tax-years.ts`).
 */
export function pastTaxYearRange(now: Date): {
  oldest: number;
  latest: number;
} {
  const current = irsCalendarYear(now);
  return { oldest: Math.max(2020, current - 5), latest: current - 1 };
}

export type WhatWasWrongResult =
  { ok: true; value: string } | { ok: false; message: string };

/** The customer's "what was wrong with the original return", trimmed once. */
export function validateWhatWasWrong(value: unknown): WhatWasWrongResult {
  const text = typeof value === "string" ? value.trim() : "";
  if (text.length < AMENDMENT_WHAT_WAS_WRONG_MIN) {
    return {
      ok: false,
      message: `Tell us what was wrong with the original return in at least ${AMENDMENT_WHAT_WAS_WRONG_MIN} characters, for example which form, line or amount was wrong.`,
    };
  }
  if (text.length > AMENDMENT_WHAT_WAS_WRONG_MAX) {
    return {
      ok: false,
      message: `Shorten what was wrong with the original return to ${AMENDMENT_WHAT_WAS_WRONG_MAX} characters or fewer. You can add detail in a message to support.`,
    };
  }
  return { ok: true, value: text };
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

export function isAmendedFiling(serviceData: unknown): boolean {
  return asRecord(serviceData).filingMode === "amended";
}

export interface AmendmentDetails {
  whatWasWrong: string | null;
  /** `documents` ids of the original return as filed (category above). */
  originalDocumentIds: string[];
  updatedAt: string | null;
}

/** `service_data.amendment` of an amended order; null for any other order. */
export function readAmendment(serviceData: unknown): AmendmentDetails | null {
  if (!isAmendedFiling(serviceData)) return null;
  const raw = asRecord(asRecord(serviceData).amendment);
  const text =
    typeof raw.whatWasWrong === "string" ? raw.whatWasWrong.trim() : "";
  const ids = Array.isArray(raw.originalDocumentIds)
    ? raw.originalDocumentIds.filter(
        (id): id is string => typeof id === "string" && id.length > 0,
      )
    : [];
  return {
    whatWasWrong: text || null,
    originalDocumentIds: ids,
    updatedAt: typeof raw.updatedAt === "string" ? raw.updatedAt : null,
  };
}

export type AmendmentRequirement = "what_was_wrong" | "original_return";

/** What an amended order still needs from the customer before it is submitted; empty otherwise. */
export function amendmentMissing(serviceData: unknown): AmendmentRequirement[] {
  const amendment = readAmendment(serviceData);
  if (!amendment) return [];
  const missing: AmendmentRequirement[] = [];
  if (!amendment.whatWasWrong) missing.push("what_was_wrong");
  if (amendment.originalDocumentIds.length === 0)
    missing.push("original_return");
  return missing;
}

export type AmendedReturnBuild =
  | { built: true }
  | { built: false; form: "Form 1120-X" | "Form 1065-X"; detail: string };

/** Whether the Worker builds an amended return for this scenario, and if not, which form the team prepares by hand. */
export function amendedReturnBuild(scenario: Scenario): AmendedReturnBuild {
  if (scenario === "ccorp") {
    return {
      built: false,
      form: "Form 1120-X",
      detail:
        "A corporation corrects a filed Form 1120 on Form 1120-X (i1120, Amended return), which we do not fill. Prepare the Form 1120-X with the corrected Form 5472 by hand.",
    };
  }
  if (scenario === "mmllc") {
    return {
      built: false,
      form: "Form 1065-X",
      detail:
        "A partnership under the BBA audit regime corrects a filed Form 1065 with an administrative adjustment request (on paper Form 1065-X marked BBA AAR, signed by the partnership representative, with Forms 8985 and 8986 when the changes pass to the members); one that elected out files Form 1065-X as an amended return. We do not fill either and no longer sell this (PG-22): talk to the customer and prepare it by hand only under a written quote.",
    };
  }
  return { built: true };
}

export interface AmendedReturnRefusal {
  title: string;
  explanation: string;
}

/** The refusal a multi-member LLC's amended return gets before payment (module comment). */
export const AMENDED_PARTNERSHIP_REFUSAL: AmendedReturnRefusal = {
  title: "We can't prepare a corrected partnership return",
  explanation:
    "How a multi-member LLC corrects a Form 1065 it already filed depends on the IRS audit rules it is under. A partnership under the centralized partnership audit regime files an administrative adjustment request (AAR): on paper, Form 1065-X with the \"BBA AAR\" box checked. An AAR needs your partnership representative's signature and works out any imputed underpayment, which the LLC pays when it files unless it passes the changes on to the members with Form 8985 and a Form 8986 for each member, which needs each member's US tax ID. It must be filed within 3 years of the later of the original due date and the date the return was filed. An LLC that elected out of these rules on Schedule B-2 files an amended return (Form 1065-X) with amended Schedules K-1 instead. We don't prepare either yet. Ask your partnership representative and a US tax professional to file it, or write to support@nonresident.tax and we will look at your case.",
};

/**
 * Null when we sell an amended return for this scenario or billing kind (a
 * C-corp is sold and prepared by hand after payment), else what to tell the
 * customer before payment: a multi-member LLC (module comment).
 */
export function amendedReturnRefusal(
  scenarioOrKind: Scenario | EntityKind,
): AmendedReturnRefusal | null {
  const kind = isEntityKind(scenarioOrKind)
    ? scenarioOrKind
    : entityKindOfScenario(scenarioOrKind);
  return kind === "mmllc" ? AMENDED_PARTNERSHIP_REFUSAL : null;
}
